import { EFileUploadStatus } from "@frontend-shared/types";
import {
  formatUploadSizeLabel,
  isTransferInFlight,
  resolveUploadProgressLabel,
} from "@frontend-shared/utils";
import { memo } from "react";

import { cn } from "@/lib/utils";

import type { ITransferPopupProgressProps } from "./types";

// NOTE: NSAI-7067 — the card's bar and its percentage. A single transfer reads
// its label exactly as its Uploads row does (shared `resolveUploadProgressLabel`
// — a percentage, a byte count for a length-less stream, or "Preparing…"); a
// batch shows the mean of everything still moving. Settled cards have no bar.
const TransferPopupProgressComponent = ({
  primary,
  selection,
  isMultiple,
  labelledById,
}: ITransferPopupProgressProps): JSX.Element | null => {
  if (!isMultiple && !isTransferInFlight(primary.status)) {
    return null;
  }

  const isFetching = primary.status === EFileUploadStatus.FETCHING;
  const isIndeterminate =
    !isMultiple &&
    (primary.status === EFileUploadStatus.CONFIRMING ||
      (isFetching && (primary.progress === 0 || primary.progress >= 100)));

  const progress = isMultiple ? selection.aggregateProgress : primary.progress;

  const resolveLabel = (): string => {
    if (isMultiple) {
      return `${selection.aggregateProgress}%`;
    }

    const label = resolveUploadProgressLabel({
      status: primary.status,
      progress: primary.progress,
      downloadedBytes: primary.downloadedBytes,
    });

    return label.percent !== undefined
      ? `${label.percent}%`
      : label.sizeInBytes !== undefined
        ? formatUploadSizeLabel({ sizeInBytes: label.sizeInBytes })
        : "";
  };

  const label = resolveLabel();

  return (
    <div className="mt-2.5 flex items-center gap-2.5">
      <div
        className="relative h-2 flex-1 overflow-hidden rounded-full bg-foreground/[0.08] ring-1 ring-inset ring-foreground/[0.04]"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-labelledby={labelledById}
        {...(!isIndeterminate && { "aria-valuenow": progress })}
        data-testid="transfer-popup-progress"
      >
        <div
          className={cn(
            "absolute inset-y-0 start-0 rounded-full bg-gradient-to-r from-primary/65 via-primary/85 to-primary",
            "shadow-[0_0_14px_hsl(var(--primary)/0.4)] transition-[width] duration-500 ease-out motion-reduce:transition-none",
            isIndeterminate && "w-full animate-pulse opacity-60",
          )}
          style={isIndeterminate ? undefined : { width: `${progress}%` }}
        />
      </div>
      {label && (
        <span
          className="min-w-[3rem] shrink-0 rounded-md bg-foreground/[0.055] px-1.5 py-0.5 text-end text-[11px] font-semibold leading-4 tabular-nums text-foreground ring-1 ring-inset ring-foreground/[0.035]"
          data-testid="transfer-popup-percent"
        >
          {label}
        </span>
      )}
    </div>
  );
};

export const TransferPopupProgress = memo(TransferPopupProgressComponent);
